<template>
  <div class="device-metrics-chart">
    <v-row class="mt-1 mb-1" style="align-items: center">
      <v-col cols="5">
        <v-text-field
          v-model="startDateTime"
          label="Start datetime"
          outlined
          hide-details
          dense
          type="datetime-local"
        />
      </v-col>
      <v-col cols="5">
        <v-text-field v-model="endDateTime" label="End datetime" outlined hide-details dense type="datetime-local" />
      </v-col>
      <v-col cols="2">
        <v-btn color="primary" style="min-width: 100%" :loading="loading" @click="fetchMetricsData"> Go </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="deviceMetrics.length && annotationTypeOptions.length" class="mb-0">
      <v-col cols="12" class="py-0 d-flex align-center flex-wrap">
        <span class="text-caption me-2">Annotations:</span>
        <v-chip
          v-for="t in annotationTypeOptions"
          :key="t.id"
          small
          class="mr-2 mb-1"
          :color="t.color"
          :outlined="!isAnnotationTypeEnabled(t.id)"
          :text-color="isAnnotationTypeEnabled(t.id) ? 'white' : t.color"
          @click="toggleAnnotationType(t.id)"
        >
          {{ t.label }}
        </v-chip>
      </v-col>
    </v-row>

    <v-row v-if="deviceMetrics.length">
      <v-col cols="12">
        <vue-apex-charts type="line" height="400" :options="chartOptions" :series="chartSeries"></vue-apex-charts>
      </v-col>
    </v-row>
    <v-row v-else-if="!loading">
      <v-col cols="12">
        <span class="text--secondary">No metrics data available for the selected range.</span>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import axios from "axios"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import VueApexCharts from "vue-apexcharts"
import store from "@/store"

dayjs.extend(utc)
dayjs.extend(timezone)

export default {
  name: "DeviceMetricsChart",

  components: {
    VueApexCharts,
  },

  props: {
    deviceId: {
      type: String,
      required: true,
    },
    initialStart: {
      type: String,
      default: null,
    },
    initialEnd: {
      type: String,
      default: null,
    },
    autoLoad: {
      type: Boolean,
      default: false,
    },
    annotations: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      startDateTime:
        this.initialStart || dayjs.tz(new Date(), "Pacific/Auckland").subtract(1, "day").format("YYYY-MM-DDTHH:mm"),
      endDateTime: this.initialEnd || dayjs.tz(new Date(), "Pacific/Auckland").format("YYYY-MM-DDTHH:mm"),
      deviceMetrics: [],
      deviceReboots: [],
      rangeStart: null,
      rangeEnd: null,
      metricsRequest: axios.CancelToken.source(),
      rebootsRequest: axios.CancelToken.source(),
      loading: false,
      annotationTypeStates: {},
    }
  },

  computed: {
    chartSeries() {
      if (!this.deviceMetrics.length) {
        return []
      }

      const points = [...this.deviceMetrics]
        .map(item => ({
          timestamp: dayjs.tz(item.event_data.date_time, "Pacific/Auckland").valueOf(),
          cpu_usage_percent: parseFloat(item.event_data.cpu_usage_percent),
          cpu_temp_celcius: parseFloat(item.event_data.cpu_temp_celcius),
          memory_usage_percent: parseFloat(item.event_data.memory_usage_percent),
          disk_usage_percent: parseFloat(item.event_data.disk_usage_percent),
        }))
        .sort((a, b) => a.timestamp - b.timestamp)

      const GAP_MS = 10 * 60 * 1000
      const seriesFor = key => {
        const data = []
        points.forEach((p, i) => {
          const value = Number.isFinite(p[key]) ? p[key] : null
          const prev = points[i - 1]
          if (prev && p.timestamp - prev.timestamp > GAP_MS) {
            data.push([prev.timestamp + 1, null])
          }
          data.push([p.timestamp, value])
        })

        return data
      }

      return [
        { name: "CPU Usage (%)", data: seriesFor("cpu_usage_percent") },
        { name: "CPU Temp (°C)", data: seriesFor("cpu_temp_celcius") },
        { name: "Memory Usage (%)", data: seriesFor("memory_usage_percent") },
        { name: "Disk Usage (%)", data: seriesFor("disk_usage_percent") },
      ]
    },
    chartOptions() {
      const formatNZ = (value, format) => dayjs(value).tz("Pacific/Auckland").format(format)

      const rebootAnnotations = this.isAnnotationTypeEnabled("reboot")
        ? this.deviceReboots
            .map(item => dayjs.tz(item.event_data.date_time, "Pacific/Auckland").valueOf())
            .filter(Number.isFinite)
            .map(timestamp => ({
              x: timestamp,
              strokeDashArray: 4,
              borderColor: "#FF4560",
              label: {
                text: `Reboot ${formatNZ(timestamp, "HH:mm")}`,
                borderColor: "#FF4560",
                style: { color: "#fff", background: "#FF4560" },
              },
            }))
        : []

      const extraAnnotations = this.annotations
        .filter(item => Number.isFinite(item.timestamp))
        .filter(item => this.isAnnotationTypeEnabled(item.type || "other"))
        .map(item => {
          const color = item.color || "#424242"

          return {
            x: item.timestamp,
            strokeDashArray: 4,
            borderColor: color,
            label: {
              text: `${item.label} ${formatNZ(item.timestamp, "HH:mm")}`,
              borderColor: color,
              style: { color: "#fff", background: color },
            },
          }
        })

      return {
        chart: {
          type: "line",
          toolbar: { show: true },
          zoom: { enabled: true },
        },
        colors: ["#008FFB", "#775DD0", "#FEB019", "#FF4560"],
        stroke: { curve: "straight", width: 1 },
        markers: { size: 2, strokeWidth: 0 },
        xaxis: {
          type: "datetime",
          min: this.rangeStart ?? undefined,
          max: this.rangeEnd ?? undefined,
          labels: {
            datetimeUTC: false,
            formatter: value => formatNZ(value, "DD MMM HH:mm"),
          },
        },
        yaxis: { decimalsInFloat: 1 },
        legend: { show: true, position: "top" },
        tooltip: {
          x: {
            formatter: value => formatNZ(value, "YYYY-MM-DD HH:mm"),
          },
        },
        annotations: {
          xaxis: [...rebootAnnotations, ...extraAnnotations],
        },
      }
    },
    annotationTypeOptions() {
      const types = new Map()

      if (this.deviceReboots.length) {
        types.set("reboot", { id: "reboot", label: "Reboots", color: "#FF4560" })
      }

      this.annotations.forEach(item => {
        const id = item.type || "other"
        if (!types.has(id)) {
          types.set(id, {
            id,
            label: item.typeLabel || id,
            color: item.color || "#424242",
          })
        }
      })

      return Array.from(types.values())
    },
  },

  watch: {
    deviceId() {
      if (this.autoLoad) {
        this.fetchMetricsData()
      }
    },
    initialStart(value) {
      if (value) {
        this.startDateTime = value
      }
    },
    initialEnd(value) {
      if (value) {
        this.endDateTime = value
      }
    },
  },

  mounted() {
    if (this.autoLoad && this.deviceId) {
      this.fetchMetricsData()
    }
  },

  methods: {
    isAnnotationTypeEnabled(id) {
      return this.annotationTypeStates[id] !== false
    },
    toggleAnnotationType(id) {
      this.$set(this.annotationTypeStates, id, !this.isAnnotationTypeEnabled(id))
    },
    async fetchMetricsData() {
      if (!this.deviceId) {
        return
      }

      this.loading = true

      const start = dayjs.tz(this.startDateTime, "Pacific/Auckland")
      const end = dayjs.tz(this.endDateTime, "Pacific/Auckland")
      const startDate = start.format("YYYY-MM-DD HH:mm:ss")
      const endDate = end.format("YYYY-MM-DD HH:mm:ss")

      this.rangeStart = start.valueOf()
      this.rangeEnd = end.valueOf()

      this.$emit("range-changed", {
        start: startDate,
        end: endDate,
        startMs: this.rangeStart,
        endMs: this.rangeEnd,
      })

      const [metricsResult, rebootsResult] = await Promise.allSettled([
        this.fetchDeviceMetrics(startDate, endDate),
        this.fetchDeviceReboots(startDate, endDate),
      ])

      if (metricsResult.status === "fulfilled") {
        this.deviceMetrics = metricsResult.value || []
      } else if (!axios.isCancel(metricsResult.reason)) {
        console.error("Failed to load device metrics:", metricsResult.reason)
        this.deviceMetrics = []
      }

      if (rebootsResult.status === "fulfilled") {
        this.deviceReboots = rebootsResult.value || []
      } else if (!axios.isCancel(rebootsResult.reason)) {
        console.error("Failed to load device reboots:", rebootsResult.reason)
        this.deviceReboots = []
      }

      this.loading = false
    },
    fetchDeviceMetrics(startDate, endDate) {
      this.metricsRequest.cancel("New fetch requested")
      this.metricsRequest = axios.CancelToken.source()

      return store.dispatch("getDeviceEventsByType", {
        event_type: "diagnostics",
        start_datetime: startDate,
        end_datetime: endDate,
        device_id: this.deviceId,
        cancelToken: this.metricsRequest.token,
      })
    },
    fetchDeviceReboots(startDate, endDate) {
      this.rebootsRequest.cancel("New fetch requested")
      this.rebootsRequest = axios.CancelToken.source()

      return store.dispatch("getDeviceEventsByType", {
        event_type: "reboots",
        start_datetime: startDate,
        end_datetime: endDate,
        device_id: this.deviceId,
        cancelToken: this.rebootsRequest.token,
      })
    },
  },
}
</script>
